/**
 * Simple retry utility for handling AbortErrors in video operations.
 * Specifically designed for cases where play() calls are interrupted by pause() calls.
 */

export type RetryOptions = {
  maxAttempts?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  maxDelayMs?: number;
};

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  delayMs: 100,
  backoffMultiplier: 1.5,
  maxDelayMs: 1000,
};

/**
 * Retries a function that returns a Promise, specifically handling AbortErrors.
 * Uses exponential backoff with jitter to avoid thundering herd problems.
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const opts = {...DEFAULT_OPTIONS, ...options};
  let lastError: Error;
  let delay = opts.delayMs;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Only retry on AbortError or DOMException with AbortError name
      const isAbortError =
        error instanceof Error &&
        (error.name === 'AbortError' || (error as any).code === 20); // DOMException.ABORT_ERR

      if (!isAbortError || attempt === opts.maxAttempts) {
        throw lastError;
      }

      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.1 * delay;
      const totalDelay = Math.min(delay + jitter, opts.maxDelayMs);

      await new Promise(resolve => setTimeout(resolve, totalDelay));
      delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelayMs);
    }
  }

  throw lastError!;
};
